import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileStack, Plus, Loader2, Building2, ChevronRight } from 'lucide-react';
import { plansApi, PageShell, PageHeader, StatusBadge, CARD, PRIMARY, STATUS, inr, dateShort, errMsg } from './shared';

const BrandCard = ({ b, i }) => (
  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className={`${CARD} p-5 flex flex-col gap-4`} data-testid={`agency-brand-${b.id}`}>
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[#FF451A]/10 text-[#FF451A] flex items-center justify-center font-bold">{b.brand_name[0]}</div>
      <div className="font-semibold text-[#0F0F0F] dark:text-white">{b.brand_name}</div>
    </div>
    <div className="flex flex-wrap gap-1.5">{Object.entries(b.counts).filter(([, n]) => n).map(([s, n]) => <span key={s} className="text-[11px] text-[#6B6B6B]">{STATUS[s].label}: <b className="text-[#0F0F0F] dark:text-white">{n}</b></span>)}</div>
    <Link to={`/agency/plans/new?brand=${b.id}`} className={`${PRIMARY} self-start`} data-testid={`agency-new-plan-${b.id}`}><Plus size={12} /> New submission</Link>
  </motion.div>
);

const PlanRow = ({ p }) => (
  <Link to={`/agency/plans/${p.id}`} className="flex items-center gap-4 px-5 py-4 border-t border-black/[0.06] dark:border-white/5 hover:bg-[#FF451A]/[0.03] transition-colors" data-testid={`agency-plan-row-${p.id}`}>
    <div className="flex-1 min-w-0">
      <div className="font-semibold text-sm text-[#0F0F0F] dark:text-white truncate">{p.campaign_name || 'Untitled plan'}</div>
      <div className="text-xs text-[#6B6B6B]">{p.brand_name} · {p.creator_count} creators · {p.submitted_at ? `submitted ${dateShort(p.submitted_at)}` : `created ${dateShort(p.created_at)}`}</div>
    </div>
    <span className="font-mono text-sm text-[#0F0F0F] dark:text-white">{inr(p.total_budget)}</span>
    <StatusBadge status={p.status} testid={`agency-plan-status-${p.id}`} />
    <ChevronRight size={14} className="text-[#A1A1AA]" />
  </Link>
);

export default function AgencyPlansHome() {
  const [me, setMe] = useState(null);
  const [plans, setPlans] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    plansApi.get('/me').then(setMe).catch((e) => setErr(errMsg(e, 'Could not load your agency')));
    plansApi.get('').then((d) => setPlans(d.plans)).catch(() => setPlans([]));
  }, []);
  return (
    <PageShell testid="agency-plans-page">
      <PageHeader icon={FileStack} title="Brand Plans" sub={me ? `${me.agency.agency_name} · pitch decks and creator sheets for your brand clients` : 'Pitch decks and creator sheets for your brand clients'} />
      {err ? <div className={`${CARD} p-6 text-sm text-[#6B6B6B]`} data-testid="agency-plans-error">{err}</div> : !me ? <Loader2 className="animate-spin" size={16} /> : (
        <>
          <section>
            <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[#6B6B6B] mb-3 inline-flex items-center gap-1.5"><Building2 size={12} /> Brand assignments</div>
            {!me.brands.length ? <div className={`${CARD} p-6 text-sm text-[#6B6B6B]`} data-testid="agency-no-brands">No brands have assigned your agency yet.</div>
              : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{me.brands.map((b, i) => <BrandCard key={b.id} b={b} i={i} />)}</div>}
          </section>
          <section className={`${CARD} overflow-hidden`} data-testid="agency-plans-list">
            <div className="px-5 py-4 font-semibold text-[#0F0F0F] dark:text-white">Your submissions</div>
            {plans === null ? <div className="px-5 pb-5"><Loader2 className="animate-spin" size={16} /></div> : !plans.length
              ? <div className="px-5 pb-6 text-sm text-[#6B6B6B]">No submissions yet — start one from a brand above.</div>
              : plans.map((p) => <PlanRow key={p.id} p={p} />)}
          </section>
        </>
      )}
    </PageShell>
  );
}
